import {compact, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {BatchSchema} from 'constants/SampleConstants';
import {tryParse} from 'notebook/data-tables/DatetimeFields';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {ClearSearchButtonProps} from 'search/ClearSearchButton';
import {SaveSearchButtonProps} from 'search/saved-searches/SaveSearchButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize} from 'ui-library/buttons/Button';

import * as FilterUtil from '../FilterUtil';
import {
  Filter,
  FILTER_KEY_TO_OPERATORS,
  FilterErrors,
  FilterKey,
  FILTERS,
  GroupByKey,
  isDatetimeFilter,
  isFieldFilter,
  isStandardDatetimeFilter,
  isStandardFilter,
  isStandardFilterRangeValue,
  isStandardFilterScalarValue,
  keyToEmptyFilter,
  Operator,
  RELATIVE_RANGE_SHORTCUT_OPERATORS,
  QuickFilterKey,
  FieldFilter,
  FILTERS_HANDLING_LOCKED,
} from '../SearchConstants';
import {componentForFilterKey, FilterOption} from './base/FilterComponents';
import {BaseFilterFormProps, FilterGroup, FilterStyle} from './base/FilterFormConstants';
import QuickFilterButton from './base/QuickFilterButton';
import SecondaryFilterDropdownSkeleton from './base/SecondaryFilterDropdownSkeleton';
import GroupByForm from './GroupByForm';
import {finalizeRelativeRangeFilter} from './relativeRangeFilterUtils';
import {getEmptyFilterErrors, reduceErrors, sanitizeTypes, validateFilters} from './ValidateFilters';

/**
 * Convenience hook that calling components can use to hold secondary dropdown state.
 */
export function useSecondaryDropdownState(): {
  isDropdownOpen: boolean;
  onSetDropdownOpen: (isDropdownOpen: boolean) => void;
} {
  const [isDropdownOpen, onSetDropdownOpen] = useState(false);
  return {isDropdownOpen, onSetDropdownOpen};
}

interface SecondaryFilterProps {
  filterGroups: Array<FilterGroup>;
  isDropdownOpen: boolean;
  onSetDropdownOpen: (isDropdownOpen: boolean) => void;
}

interface FilterContainerPropsWithoutDefaults {
  primaryFilterOptions: Array<FilterOption>;
  // If specified, a secondary filters dropdown is shown with the specified groups and state.
  secondaryFilterProps?: SecondaryFilterProps;
  filters: Array<Filter>;
  filterErrors?: FilterErrors;
  selectedSchema: Schema | BatchSchema | null;
  onChangeFilters: (filters: Array<Filter>) => void;
  filterAvailableKeys?: (filterKeys: Array<FilterKey>) => Array<FilterKey>;
  saveSearchButton?: React.ReactElement<SaveSearchButtonProps>;
  aiAssistedSearchButton?: JSX.Element | null;
  sortDropdown?: JSX.Element | null;
  disabled?: boolean;
  clearButton?: React.ReactElement<ClearSearchButtonProps> | null;

  availableQuickFilters?: Array<QuickFilterKey>;
  filterAvailableQuickFilters?: (quickFilterKeys: Array<QuickFilterKey>) => Array<QuickFilterKey>;

  availableGroupByFields?: Array<GroupByKey>;
  selectedGroupByField?: GroupByKey;
  onChangeGroupByField?: (key: string) => void;
}

const FILTERS_CONTAINER_DEFAULT_PROPS = {
  filterAvailableKeys: (filterKeys: Array<FilterKey>) => filterKeys,
};

type FiltersContainerDefaultPropTypes = Pick<
  FilterContainerPropsWithoutDefaults,
  keyof typeof FILTERS_CONTAINER_DEFAULT_PROPS
>;

/**
 * Export a version of the props that marks keys with default values as optional.
 * This matches what a user would need to pass into a FiltersContainer. This is necessary
 * because ListingFiltersContainer reuses FiltersContainerProps and ListingFiltersContainer does not
 * (and should not) provide its own default props.
 */
export type FiltersContainerProps = Omit<
  FilterContainerPropsWithoutDefaults,
  keyof FiltersContainerDefaultPropTypes
> &
  Partial<FiltersContainerDefaultPropTypes>;

interface FiltersContainerState {
  temporarySecondaryFilters: Array<Filter>;
  dirtySecondaryFilterIndexes: Array<number>;
}

type FilterValue = Filter['value'];
const NULL_VALUES: Array<FilterValue> = [[], null];
function isNullValue(filterValue: FilterValue): boolean {
  return NULL_VALUES.some((nullValue) => isEqual(nullValue, filterValue));
}

export default class FiltersContainer extends React.PureComponent<
  FilterContainerPropsWithoutDefaults,
  FiltersContainerState
> {
  static defaultProps = FILTERS_CONTAINER_DEFAULT_PROPS;

  private getPrimaryFilterKeys(): Array<FilterKey> {
    return this.props.primaryFilterOptions.map((filterOption) => filterOption.filterKey);
  }

  private getSecondaryFilterKeys(otherProps?: FiltersContainerProps): Array<FilterKey> {
    const props = otherProps ?? this.props;
    const secondaryFilterGroups = props.secondaryFilterProps?.filterGroups ?? [];
    return secondaryFilterGroups.flatMap((filterGroup) =>
      filterGroup.filterOptions.map((filterOption) => filterOption.filterKey)
    );
  }

  private getActiveSecondaryFilters(otherProps?: FiltersContainerProps): Array<Filter> {
    const props = otherProps ?? this.props;
    return props.filters.filter((filter) => this.getSecondaryFilterKeys(props).includes(filter.key));
  }

  private getHaveSecondaryFiltersChanged(): boolean {
    return !isEqual(this.getActiveSecondaryFilters(), this.state.temporarySecondaryFilters);
  }

  private getFilterDependencies(filterKey: FilterKey): Array<Filter> {
    if (!FILTERS[filterKey].dependencies || FILTERS[filterKey].dependencies.length === 0) {
      return [];
    }

    // This includes primary filters and any filters that are not accessible via the FiltersContainer
    // (i.e. they are hidden filters added by the listing).
    const nonSecondaryFilters = this.props.filters.filter(
      (filter) => !this.getSecondaryFilterKeys().includes(filter.key)
    );
    const allCandidateFilters = this.state.temporarySecondaryFilters.concat(nonSecondaryFilters);

    return allCandidateFilters.filter((filter) => FILTERS[filterKey].dependencies!.includes(filter.key));
  }

  override UNSAFE_componentWillReceiveProps(nextProps: FiltersContainerProps): void {
    if (this.props.filters !== nextProps.filters) {
      this.setState({
        temporarySecondaryFilters: this.getActiveSecondaryFilters(nextProps),
        dirtySecondaryFilterIndexes: [],
      });
    }
  }

  constructor(props: FilterContainerPropsWithoutDefaults) {
    super(props);
    if (props.filterErrors) {
      assert(
        props.filters.length === props.filterErrors.length,
        'Expected filters and filterErrors to have the same length'
      );
    }
    assert(
      _.intersection(this.getPrimaryFilterKeys(), this.getSecondaryFilterKeys()).length === 0,
      'primaryFilterKeys and secondaryFilterKeys cannot intersect'
    );
    assert(
      // eslint-disable-next-line no-restricted-syntax
      _.uniq(this.getPrimaryFilterKeys()).length === this.getPrimaryFilterKeys().length,
      'Each element of primaryFilterKeys must be unique'
    );
    this.state = {
      temporarySecondaryFilters: this.getActiveSecondaryFilters(),
      dirtySecondaryFilterIndexes: [],
    };
  }

  private filterSkeletonProps(
    filter: Filter,
    filterStyle: FilterStyle
  ): BaseFilterFormProps & {
    filter: Filter;
    filters: Array<Filter>;
    onChangeFilters: (filters: Array<Filter>) => void;
    filterStyle: FilterStyle;
    operators: Array<Operator>;
    selectedOperator: Operator;
    isDisabled?: boolean;
  } {
    assert(
      !filter.locked || FILTERS_HANDLING_LOCKED.includes(filter.key),
      'Encountered locked filter key that does not support locking. The corresponding filter component should be modified to disable inputs and the key should be added to `FILTERS_HANDLING_LOCKED`.'
    );
    return {
      buttonSize: ButtonSize.SMALL,
      dropdownToggleText: FILTERS[filter.key].name,
      filter,
      filterStyle,
      isActive: !isNullValue(filter.value),
      operators: FILTER_KEY_TO_OPERATORS[filter.key],
      selectedOperator: filter.operator,
      filters: this.props.filters,
      onChangeFilters: this.props.onChangeFilters,
      isDisabled: this.props.disabled,
      dependencies: this.getFilterDependencies(filter.key),
      locked: filter.locked,
    };
  }

  private primaryFilterKeyToComponent(filterOption: FilterOption, filterErrors: FilterErrors): JSX.Element {
    const filterKey = filterOption.filterKey;
    const index = this.props.filters.findIndex((f) => f.key === filterKey);
    const filter = this.props.filters[index] || keyToEmptyFilter(filterKey);
    const filterError = filterErrors[index];
    const FilterComponentClass = componentForFilterKey(filter.key);
    if (!FilterComponentClass) {
      throw new Error(`Unexpected filter key ${filter.key}`);
    }
    return (
      <FilterComponentClass
        {...this.filterSkeletonProps(filter, FilterStyle.PRIMARY)}
        {...filterOption.extraProps}
        invalidMessage={filterError}
        onChangeFilter={(newFilter: Filter) => {
          this.props.onChangeFilters(FilterUtil.replaceByKey(this.props.filters, newFilter));
        }}
        prefinalizedFilters={this.getPrefinalizedFilters()}
      />
    );
  }

  private removeSecondaryFilters(indexesToRemove: Array<number>): void {
    const sortedIndexesToRemove = [...indexesToRemove].sort((a, b) => b - a);
    const updatedDirtyIndexes = this.state.dirtySecondaryFilterIndexes
      .filter((dirtyIndex) => !indexesToRemove.includes(dirtyIndex))
      .map((dirtyIndex) => {
        let decrement = 0;
        for (const removedIndex of sortedIndexesToRemove) {
          if (removedIndex < dirtyIndex) {
            decrement++;
          } else {
            // Since indexesToRemove is sorted, we can break early
            break;
          }
        }
        return dirtyIndex - decrement;
      });

    this.setState({
      dirtySecondaryFilterIndexes: updatedDirtyIndexes,
    });
    this.handleChangeSecondaryFilters([
      ...this.state.temporarySecondaryFilters.filter((_, i) => !indexesToRemove.includes(i)),
    ]);
  }

  private secondaryFilterToComponent(
    filter: Filter,
    filterError: FilterErrors[number],
    index: number
  ): JSX.Element {
    const FilterComponentClass = componentForFilterKey(filter.key);
    if (!FilterComponentClass) {
      throw new Error(`Unexpected filter key ${filter.key}`);
    }
    const secondaryFilterGroups = this.props.secondaryFilterProps?.filterGroups ?? [];
    const filterOption = secondaryFilterGroups
      .flatMap((filterGroup) => filterGroup.filterOptions)
      .find(({filterKey}) => filterKey === filter.key);

    if (!filterOption) {
      throw new Error(`Unexpected filter key ${filter.key}`);
    }
    const availableFilterKeys = this.props.filterAvailableKeys!(this.getSecondaryFilterKeys());
    const secondaryFilterProps = {
      filterGroups: compact(
        secondaryFilterGroups.map((filterGroup) => {
          if (filterGroup.filterOptions.every(({filterKey}) => !availableFilterKeys.includes(filterKey))) {
            return null;
          }
          return {
            ...filterGroup,
            filterOptions: filterGroup.filterOptions.filter(({filterKey}) =>
              availableFilterKeys.includes(filterKey)
            ),
          };
        })
      ),
      onChangeFilterKey: (key: FilterKey) => {
        this.handleUpdateSecondaryFilter(keyToEmptyFilter(key), index);
      },
      selectedFilterKey: filter.key,
      onRemove: () => {
        this.removeSecondaryFilters([index]);
      },
    };
    const sharedProps = {
      ...this.filterSkeletonProps(filter, FilterStyle.SECONDARY),
      ...secondaryFilterProps,
    };
    return (
      <FilterComponentClass
        {...sharedProps}
        {...filterOption.extraProps}
        invalidMessage={filterError}
        onChangeFilter={(newFilter: Filter) => {
          this.handleUpdateSecondaryFilter(newFilter, index);
        }}
        onEnter={this.handleSecondaryFilterEnter}
        prefinalizedFilters={this.getPrefinalizedFilters()}
        isDirty={this.state.dirtySecondaryFilterIndexes.includes(index)}
      />
    );
  }

  private getAvailableQuickFilters(): Array<QuickFilterKey> {
    if (this.props.filterAvailableQuickFilters != null) {
      return this.props.filterAvailableQuickFilters(this.props.availableQuickFilters ?? []);
    } else {
      return this.props.availableQuickFilters ?? [];
    }
  }

  private quickFilterButtons(): JSX.Element | undefined {
    const availableQuickFilters = this.getAvailableQuickFilters();
    return availableQuickFilters.length > 0 ? (
      <div className={css(styles.quickFilterSectionWrapper)}>
        <div className={css(styles.quickFilterTitleText)}>Quick filters</div>
        <div className={css(styles.quickFilterButtonWrapper)}>
          {availableQuickFilters.map((quickFilterKey) => (
            <QuickFilterButton
              key={quickFilterKey}
              quickFilterKey={quickFilterKey}
              selectedFilters={this.state.temporarySecondaryFilters}
              onAddQuickFilter={(filtersToAdd) => {
                this.handleChangeSecondaryFilters([...this.state.temporarySecondaryFilters, ...filtersToAdd]);
              }}
              onRemoveQuickFilter={(filterIndexesToRemove) => {
                this.removeSecondaryFilters(filterIndexesToRemove);
              }}
            />
          ))}
        </div>
      </div>
    ) : undefined;
  }

  private handleChangeSecondaryFilters = (temporarySecondaryFilters: Array<Filter>): void => {
    this.setState({temporarySecondaryFilters});
  };

  private handleAddSecondaryFilter = (): void => {
    this.setState({
      dirtySecondaryFilterIndexes: _.union(this.state.dirtySecondaryFilterIndexes, [
        this.state.temporarySecondaryFilters.length,
      ]),
    });
    this.handleChangeSecondaryFilters([
      ...this.state.temporarySecondaryFilters,
      keyToEmptyFilter(this.getSecondaryFilterKeys()[0]),
    ]);
  };

  private handleUpdateSecondaryFilter(newFilter: Filter, index: number): void {
    const newSecondaryFilters = this.state.temporarySecondaryFilters.slice();
    newSecondaryFilters[index] = {
      ...newSecondaryFilters[index],
      ...newFilter,
    };
    this.setState({
      dirtySecondaryFilterIndexes: _.union(this.state.dirtySecondaryFilterIndexes, [index]),
    });
    this.handleChangeSecondaryFilters(newSecondaryFilters);
  }

  private getPrefinalizedFilters(): Array<Filter> {
    const activeSecondaryFilters = this.getActiveSecondaryFilters();
    return [
      ...this.props.filters.filter((filter) => !activeSecondaryFilters.includes(filter)),
      ...this.state.temporarySecondaryFilters,
    ];
  }

  _handleFinalizeSecondaryFilters = (): void => {
    const newFilters = this.getPrefinalizedFilters();
    // No-op if filters haven't changed.
    if (isEqual(this.props.filters, newFilters)) {
      return;
    }

    const adaptedFilters = newFilters.map((filter): Filter => {
      if (isDatetimeFilter(filter)) {
        return {
          ...filter,
          value: {
            fromDatetime: filter.value.fromDatetime ? tryParse(filter.value.fromDatetime) : null,
            toDatetime: filter.value.toDatetime ? tryParse(filter.value.toDatetime) : null,
          },
        };
      } else if (
        (isStandardDatetimeFilter(filter) ||
          (isFieldFilter(filter) && (filter as FieldFilter).fieldType === FieldType.DATETIME)) &&
        !(
          filter.operator === Operator.RELATIVE_RANGE ||
          RELATIVE_RANGE_SHORTCUT_OPERATORS.includes(filter.operator)
        )
      ) {
        const value = filter.value as string;
        if (isStandardFilterRangeValue(value)) {
          return {
            ...filter,
            value: {
              from: value.from ? tryParse(value.from as string) : null,
              to: value.to ? tryParse(value.to as string) : null,
            },
          };
        } else if (isStandardFilterScalarValue(value)) {
          return {
            ...filter,
            value: value ? tryParse(value) : null,
          };
        } else {
          return filter;
        }
      } else if (
        isStandardFilter(filter) &&
        (filter.operator === Operator.RELATIVE_RANGE ||
          RELATIVE_RANGE_SHORTCUT_OPERATORS.includes(filter.operator))
      ) {
        return finalizeRelativeRangeFilter(filter);
      } else {
        return filter;
      }
    });
    this.setState({
      dirtySecondaryFilterIndexes: [],
    });
    this.props.onChangeFilters(adaptedFilters);
  };

  private getPrefinalizedSecondaryFilterErrors(): FilterErrors {
    let filters = this.getPrefinalizedFilters();
    let typeErrors: FilterErrors = [];

    // Since the types get sanitized (narrowed to types that are compatible with selected filters)
    // when secondary filters are applied, we want to also sanitize them here to let the user update them
    // without validation errors. But, we skip sanitization when the search filters are finalized to
    // show validation errors between incompatible types and finalized secondary filters
    if (this.getHaveSecondaryFiltersChanged()) {
      const sanitizedTypes = sanitizeTypes(filters, this.props.selectedSchema);
      filters = sanitizedTypes.filters;
      typeErrors = sanitizedTypes.typeErrors;
    }
    const baseErrors = validateFilters(filters, this.props.selectedSchema);
    return this.state.temporarySecondaryFilters.length > 0
      ? reduceErrors(typeErrors, baseErrors).slice(-this.state.temporarySecondaryFilters.length)
      : [];
  }

  private handleToggleSecondaryDropdown = (isSecondaryDropdownOpen: boolean): void => {
    if (this.props.secondaryFilterProps == null) {
      throw new Error('Unexpected secondary dropdown toggle with secondary dropdown disabled.');
    }
    this.props.secondaryFilterProps.onSetDropdownOpen(isSecondaryDropdownOpen);
    if (!isSecondaryDropdownOpen) {
      this._handleFinalizeSecondaryFilters();
    }
  };

  private handleSecondaryFilterEnter = (): void => {
    this.handleToggleSecondaryDropdown(false);
  };

  override render(): JSX.Element {
    const filterErrors = this.props.filterErrors || getEmptyFilterErrors(this.props.filters);
    const secondaryFilterErrors = this.getPrefinalizedSecondaryFilterErrors();
    const secondaryFilterNodes = this.state.temporarySecondaryFilters.map((filter, index) =>
      this.secondaryFilterToComponent(filter, secondaryFilterErrors[index], index)
    );
    const hasSecondaryFilterErrors = secondaryFilterErrors.some((error) => error != null);
    return (
      <div className={css(styles.wrapper)}>
        {this.props.primaryFilterOptions.map((filterOption) => (
          <div
            className={css(styles.form)}
            key={filterOption.filterKey}
            {...getTestProps({
              component: 'FiltersContainer',
              element: 'filterOption',
              key: filterOption.filterKey,
            })}
          >
            {this.primaryFilterKeyToComponent(filterOption, filterErrors)}
          </div>
        ))}
        {this.props.secondaryFilterProps != null && (
          <div className={css(styles.form)}>
            <SecondaryFilterDropdownSkeleton
              buttonSize={ButtonSize.SMALL}
              secondaryFilterNodes={secondaryFilterNodes}
              hasFilterErrors={hasSecondaryFilterErrors}
              onAddFilter={this.handleAddSecondaryFilter}
              isOpen={this.props.secondaryFilterProps.isDropdownOpen}
              onToggle={this.handleToggleSecondaryDropdown}
              disabled={this.props.disabled}
              quickFiltersSection={this.quickFilterButtons()}
            />
          </div>
        )}

        {this.props.availableGroupByFields?.length &&
        this.props.availableGroupByFields[0] !== GroupByKey.BLAST_QUERY ? (
          <div className={css(styles.form)}>
            <GroupByForm
              options={this.props.availableGroupByFields}
              value={this.props.selectedGroupByField ?? GroupByKey.NONE}
              onChange={this.props.onChangeGroupByField ?? noop}
              disabled={this.props.disabled}
              {...getTestProps({
                component: 'FiltersContainer',
                element: 'GroupByForm',
              })}
            />
          </div>
        ) : null}

        {this.props.sortDropdown ? <div className={css(styles.form)}>{this.props.sortDropdown}</div> : null}

        {this.props.aiAssistedSearchButton ? (
          <div className={css(styles.form)}>{this.props.aiAssistedSearchButton}</div>
        ) : null}

        {this.props.saveSearchButton ? (
          <div className={css(styles.form)}>{this.props.saveSearchButton}</div>
        ) : null}

        {this.props.clearButton && <div className={css(styles.clearButton)}>{this.props.clearButton}</div>}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  quickFilterSectionWrapper: {
    margin: 10,
  },
  quickFilterTitleText: {
    marginBottom: 6,
  },
  quickFilterButtonWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  form: {
    marginRight: 6,
    marginBottom: 6,
  },
  linkContainer: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 12,
  },
  clearButton: {
    marginBottom: 6,
    marginLeft: 12,
    display: 'flex',
    alignItems: 'center',
  },
});
